import { useEffect, useRef } from 'react';

/**
 * Hook personalizado para detectar clicks fuera de uno o varios elementos
 * 
 * @param {Function} handler - Función callback que se ejecuta cuando hay click fuera
 * @param {Array<React.RefObject>} excludeRefs - Array de refs adicionales a excluir (ej: portales, dropdowns)
 * @returns {React.RefObject} - Ref del elemento principal a monitorear
 * 
 * @example
 * const dropdownRef = useRef(null);
 * const buttonRef = useClickOutside(
 *   () => setIsOpen(false),
 *   [dropdownRef] // No cerrará si se hace click en el dropdown
 * );
 */
export const useClickOutside = (handler, excludeRefs = []) => {
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            // 1️⃣ Verificar si el click fue dentro del elemento principal
            if (ref.current && ref.current.contains(event.target)) {
                return;
            }

            // 2️⃣ Verificar si el click fue dentro de algún elemento excluido
            const isClickInExcludedArea = excludeRefs.some(excludeRef => 
                excludeRef?.current && excludeRef.current.contains(event.target)
            );

            if (isClickInExcludedArea) {
                return;
            }

            // 3️⃣ Si llegamos aquí, el click fue fuera de todos los elementos
            handler(event);
        };

        // Escuchar tanto mouse como touch para soporte móvil
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [handler, excludeRefs]);

    return ref;
};